import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import ErrorIcon from '@components/icons/FormError.svg';
import { forwardRef, useEffect, useState } from 'react';
import { TProps } from './types/TProps';

type InputProps = TProps & {
  multiline?: boolean;
};

const inputClassName = 'input form-control inline-block h-10 px-4';

const multilineClassName = 'input form-control block h-[120px] w-full px-4 py-3.5';

export const Input = forwardRef(function Input(
  {
    multiline = false,
    type = 'text',
    name = 'input',
    icon: Icon,
    adornment,
    label,
    onChange,
    onIconClick,
    iconButtonLabel,
    value = '',
    placeholder = '',
    min = undefined,
    max = undefined,
    step = undefined,
    inputMode = 'text',
    required = false,
    disabled = false,
    readOnly = false,
    error = false,
    className = 'form-input w-full',
    handleBlur,
    size,
    onKeyUp,
    ...rest
  }: InputProps,
  ref: React.Ref<HTMLInputElement | HTMLTextAreaElement>,
) {
  const defaultTitle = `${label}${required ? ' *' : ''}`;
  const placeholderTitle = placeholder ? placeholder : defaultTitle;
  const [title, setTitle] = useState(defaultTitle);
  const withIcon = Boolean(Icon) || Boolean(adornment) || error;

  useEffect(() => {
    setTitle(defaultTitle);
  }, [defaultTitle]);

  const onFocus = () => {
    setTitle(placeholderTitle);
  };

  const onBlur = () => {
    setTitle(defaultTitle);
    handleBlur?.(null);
  };

  const renderIcon = () => {
    if (adornment) {
      return <FormFieldIcon>{adornment}</FormFieldIcon>;
    }

    if (!Icon) {
      return null;
    }

    if (error && !onIconClick) {
      return (
        <FormFieldIcon>
          <ErrorIcon aria-hidden="true" focusable="false" />
        </FormFieldIcon>
      );
    }

    if (!onIconClick) {
      return (
        <FormFieldIcon>
          <Icon aria-hidden="true" focusable="false" />
        </FormFieldIcon>
      );
    }

    return (
      <FormFieldIcon clickable>
        <IconButtonClean type="button" onClick={onIconClick} aria-label={iconButtonLabel}>
          <Icon aria-hidden="true" focusable="false" />
        </IconButtonClean>
      </FormFieldIcon>
    );
  };

  const controlClassName = [
    multiline ? multilineClassName : inputClassName,
    withIcon ? 'pr-[45px]' : '',
    error ? 'form-control--error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const assignRef = (node: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>).current = node;
    }
  };

  return (
    <FormFieldRoot disabled={disabled} className={className}>
      {multiline ? (
        <textarea
          ref={assignRef}
          className={controlClassName}
          placeholder={title}
          aria-label={label}
          aria-invalid={error || undefined}
          name={name}
          onChange={onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
          value={value}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={onKeyUp as React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>}
          {...rest}
        />
      ) : (
        <input
          ref={assignRef}
          className={controlClassName}
          placeholder={title}
          aria-label={label}
          aria-invalid={error || undefined}
          name={name}
          onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
          value={value}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={onKeyUp as React.KeyboardEventHandler<HTMLInputElement>}
          type={type}
          min={min}
          max={max}
          step={step}
          inputMode={inputMode}
          size={size}
          {...rest}
        />
      )}

      {renderIcon()}
    </FormFieldRoot>
  );
});
